import { CheckIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import {
  Menu,
  MenuItem,
  type MenuItemProps,
  type MenuProps,
  MenuTrigger,
  MenuSection as RACMenuSection,
  type SeparatorProps,
  composeRenderProps,
} from 'react-aria-components';
import { composeTailwindRenderProps } from '../../utils';
import { Popover, type PopoverProps } from '../popover';
import { Separator } from '../separator';

const _MenuTrigger = MenuTrigger;

const MenuSection = RACMenuSection;

function MenuPopover(props: PopoverProps) {
  const { className, offset = 4, ...other } = props;

  return (
    <Popover
      className={composeTailwindRenderProps(className, 'overflow-y-auto outline-none rounded-none')}
      offset={offset}
      {...other}
    />
  );
}

function _Menu<T extends object>({ className, ...props }: MenuProps<T>) {
  return <Menu className={cx('py-4 outline-none min-w-[9rem]', className)} {...props} />;
}

interface _MenuItemProps extends MenuItemProps {}

function _MenuItem(props: _MenuItemProps) {
  const { children: childrenProp, className, ...other } = props;

  return (
    <MenuItem
      className={composeTailwindRenderProps(
        className,
        cx(
          'h-21 relative flex cursor-default select-none items-center gap-4 py-4 px-5 mx-3 outline-none transition-colors',
          'data-[disabled]:pointer-events-none data-[disabled]:text-disabled data-[disabled]:bg-primary-disabled',
          'text-primary text-md data-[focused]:bg-primary-hover data-[selected]:data-[focused]:text-primary',
        ),
      )}
      {...other}
    >
      {composeRenderProps(childrenProp, (children, { isSelected }) => (
        <>
          {children}
          <span className="absolute right-6 flex h-10 w-10 items-center justify-center">
            {isSelected && <CheckIcon size={16} color={colors.brand['600']} />}
          </span>
        </>
      ))}
    </MenuItem>
  );
}

function MenuSeparator(props: SeparatorProps) {
  const { className, ...other } = props;

  return <Separator className={cx('my-2', className)} {...other} />;
}

export { _Menu as Menu, _MenuItem as MenuItem, MenuPopover, MenuSection, MenuSeparator, _MenuTrigger as MenuTrigger };
export type { _MenuItemProps as MenuItemProps, MenuProps };
